import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, Waves, Layers, Globe2, Boxes, ClipboardCheck,
  Radar, Presentation, BookOpen, TrendingUp, Building2, Store, Award,
  Download, ChevronRight,
} from "lucide-react";

const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);
const Chip = ({ children, color = BLUE, filled }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

export const PhaseXVICentre = () => {
  const [tab, setTab] = useState("narrative");
  const [profiles, setProfiles] = useState([]);
  const [trust, setTrust] = useState(null);
  const [water, setWater] = useState(null);
  const [catchments, setCatchments] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [dep, setDep] = useState(null);
  const [twin, setTwin] = useState(null);
  const [comp, setComp] = useState(null);
  const [ri, setRi] = useState(null);
  const [studio, setStudio] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [kcats, setKcats] = useState(null);
  const [obs, setObs] = useState(null);
  const [comm, setComm] = useState(null);
  const [market, setMarket] = useState(null);
  const [ack, setAck] = useState(null);

  const [narProfile, setNarProfile] = useState("cabinet");
  const [narSubject, setNarSubject] = useState("Phase XVI endorsement");
  const [narLength, setNarLength] = useState("short");
  const [narHistory, setNarHistory] = useState([]);
  const [narBusy, setNarBusy] = useState(false);
  const [latestNar, setLatestNar] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [np, t, w, c, tp, d, tw, cp, r, st, kc, ob, cm, mk, ak, nh] = await Promise.all([
          axios.get(`${API}/cadastre360/phase-xvi/narrative/profiles`,       { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/trust/framework`,           { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/water360/overview`,         { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/water360/catchments`,       { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/platform-template`,         { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/deployment-centre`,         { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/digital-twin/domains`,      { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/compliance`,                { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/resource-intelligence`,     { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/demonstration-studio/profiles`,{ headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/knowledge/categories`,      { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/observatory`,               { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/commercial`,                { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/marketplace`,               { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/acceptance`,                { headers: auth() }),
          axios.get(`${API}/cadastre360/phase-xvi/narrative/history`,         { headers: auth() }),
        ]);
        setProfiles(np.data.profiles); setTrust(t.data); setWater(w.data); setCatchments(c.data);
        setTpl(tp.data); setDep(d.data); setTwin(tw.data); setComp(cp.data); setRi(r.data);
        setStudio(st.data.profiles); setKcats(kc.data); setObs(ob.data); setComm(cm.data);
        setMarket(mk.data); setAck(ak.data); setNarHistory(nh.data || []);
      } catch (e) { toast.error("Load failed — " + e.message); }
    })();
  }, []);

  const generateNarrative = async () => {
    setNarBusy(true);
    try {
      const r = await axios.post(`${API}/cadastre360/phase-xvi/narrative`,
        { profile: narProfile, subject: narSubject, length: narLength },
        { headers: auth() });
      setLatestNar(r.data);
      const nh = await axios.get(`${API}/cadastre360/phase-xvi/narrative/history`, { headers: auth() });
      setNarHistory(nh.data || []);
      toast.success(`Narrative generated · ${r.data.model}`);
    } catch (e) { toast.error("Narrative failed — " + e.message); }
    finally { setNarBusy(false); }
  };

  const openStudio = async (key) => {
    try {
      const r = await axios.get(`${API}/cadastre360/phase-xvi/demonstration-studio/profiles/${key}`, { headers: auth() });
      setSelectedStudio(r.data);
    } catch (e) { toast.error("Studio failed — " + e.message); }
  };

  const TABS = [
    { key: "narrative",   label: "AI Narrative Engine",     icon: Sparkles,      accent: PURPLE },
    { key: "trust",       label: "Sovereign Digital Trust", icon: ShieldCheck,   accent: GOLD },
    { key: "water360",    label: "Water360™",               icon: Waves,         accent: BLUE },
    { key: "template",    label: "Platform Template",       icon: Layers,        accent: PURPLE },
    { key: "deployment",  label: "Deployment Centre",       icon: Globe2,        accent: GREEN },
    { key: "twin",        label: "Digital Twin Expansion",  icon: Boxes,         accent: BLUE },
    { key: "compliance",  label: "Intl Compliance",         icon: ClipboardCheck,accent: GREEN },
    { key: "resource",    label: "Resource Intelligence",   icon: Radar,         accent: AMBER },
    { key: "studio",      label: "Demonstration Studio",    icon: Presentation,  accent: PURPLE },
    { key: "knowledge",   label: "Gov Knowledge Repo",      icon: BookOpen,      accent: GOLD },
    { key: "observatory", label: "Performance Observatory", icon: TrendingUp,    accent: GREEN },
    { key: "commercial",  label: "Commercial Positioning",  icon: Building2,     accent: GOLD },
    { key: "marketplace", label: "Federation Marketplace",  icon: Store,         accent: PURPLE },
    { key: "acceptance",  label: "Executive Acceptance",    icon: Award,         accent: GREEN },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="page-phase-xvi">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: PURPLE + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${PURPLE}30 100%)` }}>
        <ShieldCheck size={38} className="mt-1" style={{ color: PURPLE }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: PURPLE }}>
            Cadastre360™ · Phase XVI · Sovereign Trust, Expansion & International Readiness
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Executive Readiness · Global Deployment
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Public verification · true LLM narrative · sovereign digital trust · Water360 federation ·
            deployment models · digital twin · international compliance · cross-pillar intelligence.
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <Chip color={GREEN} filled>Zero Regression</Chip>
          <Chip color={GOLD} filled>QR Verifiable</Chip>
          <Chip color={BLUE} filled>Internationally Ready</Chip>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={"px-3 py-1.5 text-[11.5px] flex items-center gap-1.5 font-medium border-b-2 " +
                             (tab === t.key ? "" : "border-transparent text-[#7C93A5]")}
                  style={tab === t.key ? { borderColor: t.accent, color: "#F6F8FA" } : {}}
                  data-testid={`xvi-tab-${t.key}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "narrative" && (
        <>
          <Card title="Executive AI Narrative Engine™" accent={PURPLE} testId="xvi-narrative">
            <div className="flex flex-wrap gap-2 mb-2">
              <select value={narProfile} onChange={(e) => setNarProfile(e.target.value)}
                      className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15"
                      data-testid="narrative-profile">
                {profiles.map((p) => <option key={p.key} value={p.key}>{p.title}</option>)}
              </select>
              <input value={narSubject} onChange={(e) => setNarSubject(e.target.value)}
                     className="flex-1 min-w-[240px] rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15"
                     placeholder="Subject / theme" data-testid="narrative-subject" />
              <select value={narLength} onChange={(e) => setNarLength(e.target.value)}
                      className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15">
                <option value="short">Short</option><option value="medium">Medium</option><option value="long">Long</option>
              </select>
              <button onClick={generateNarrative} disabled={narBusy}
                      className="rounded-sm border px-3 py-1.5 text-[11.5px] font-medium"
                      style={{ borderColor: PURPLE + "60", color: "#F6F8FA", background: PURPLE + "25" }}
                      data-testid="btn-generate-narrative">
                <Sparkles size={12} className="inline mr-1" /> {narBusy ? "Generating…" : "Generate"}
              </button>
            </div>
            {latestNar && (
              <div className="rounded-sm border p-3 mt-2" style={{ borderColor: PURPLE + "35", background: "rgba(7,27,43,0.55)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-[13px] font-semibold text-[#F6F8FA] flex-1">{latestNar.profile_title} — {latestNar.subject}</div>
                  <Chip color={GREEN}>{latestNar.model}</Chip>
                </div>
                <div className="text-[12px] text-[#DCE3E8] whitespace-pre-wrap max-h-[300px] overflow-auto">{latestNar.narrative}</div>
                <div className="mt-2 text-[10.5px] text-[#7C93A5] font-mono break-all">SHA-256: {latestNar.digital_signature_sha256}</div>
              </div>
            )}
            <div className="mt-3 space-y-1 max-h-[240px] overflow-auto">
              {narHistory.map((n) => (
                <div key={n.id} className="rounded-sm border px-2 py-1 flex items-center gap-2"
                     style={{ borderColor: PURPLE + "25", background: "rgba(7,27,43,0.45)" }}>
                  <Sparkles size={11} style={{ color: PURPLE }} />
                  <div className="flex-1 text-[11.5px] text-[#F6F8FA] truncate">{n.profile_title} — {n.subject}</div>
                  <div className="text-[10px] text-[#7C93A5] font-mono">{n.id}</div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {tab === "trust" && trust && (
        <Card title="Sovereign Digital Trust Framework™" accent={GOLD} testId="xvi-trust">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            {trust.capabilities.map((c) => (
              <div key={c.key} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GOLD + "40", background: "rgba(7,27,43,0.55)" }} data-testid={`trust-${c.key}`}>
                <div className="text-[11.5px] text-[#F6F8FA]">{c.label}</div>
                <Chip color={GREEN}>{c.status}</Chip>
              </div>
            ))}
          </div>
          <div className="text-[11.5px] text-[#DCE3E8]">Issuer: {trust.issuer}</div>
          <div className="text-[11.5px] text-[#DCE3E8]">Root CA: {trust.root_ca}</div>
          <div className="text-[10.5px] text-[#7C93A5] font-mono mt-2">Verify endpoint: {trust.verify_endpoint}</div>
        </Card>
      )}

      {tab === "water360" && water && (
        <>
          <Card title="Water360™ · First federated sovereign pillar" accent={BLUE} testId="xvi-water360">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              {Object.entries(water.kpis).map(([k, v]) => (
                <div key={k} className="rounded-sm border px-2 py-1.5" style={{ borderColor: BLUE + "40", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{k.replace(/_/g, ' ')}</div>
                  <div className="text-[15px] font-bold text-[#F6F8FA]" style={{ color: BLUE }}>{v.toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-[#B3C2CC] mb-2">Consumes: {water.consumes.join(" · ")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
              {water.capabilities.map((c) => (
                <div key={c.key} className="rounded-sm border px-2 py-1 flex items-center gap-2" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.45)" }}>
                  <div className="flex-1 text-[11.5px] text-[#F6F8FA]">{c.label}</div>
                  <span className="font-mono text-[11px] text-[#F6F8FA]">{c.score}</span>
                </div>
              ))}
            </div>
          </Card>
          {catchments && (
            <Card title="Water360™ · Catchment Intelligence" accent={TEAL}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                {catchments.catchments.map((c) => (
                  <div key={c.name} className="rounded-sm border p-2" style={{ borderColor: TEAL + "35", background: "rgba(7,27,43,0.5)" }}>
                    <div className="text-[12.5px] font-semibold text-[#F6F8FA]">{c.name} Catchment</div>
                    <div className="text-[10.5px] text-[#B3C2CC]">Area {c.area_km2.toLocaleString()} km² · Yield {c.water_yield_index} · Quality {c.quality_score}</div>
                    <Chip color={c.risk === "high" ? RED : c.risk === "medium" ? AMBER : GREEN}>{c.risk} risk</Chip>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {tab === "template" && tpl && (
        <Card title="Future Sovereign Platform Framework™" accent={PURPLE} testId="xvi-template">
          <div className="text-[13px] font-semibold text-[#F6F8FA] mb-2">{tpl.template}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <div>
              <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Inherited services</div>
              <div className="flex flex-wrap gap-1">{tpl.inherits.map((i) => <Chip key={i} color={PURPLE}>{i}</Chip>)}</div>
            </div>
            <div>
              <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Out of the box</div>
              <ul className="text-[11.5px] text-[#F6F8FA] list-disc pl-4">
                {tpl.provides_out_of_box.map((i) => <li key={i}>{i}</li>)}
              </ul>
            </div>
          </div>
          <div className="text-[12px] text-[#DCE3E8]">Effort reduction: <span className="font-bold" style={{ color: GREEN }}>{tpl.estimated_effort_reduction_pct}%</span> · Reference: {tpl.reference_implementation}</div>
        </Card>
      )}

      {tab === "deployment" && dep && (
        <Card title="International Deployment Centre™" accent={GREEN} testId="xvi-deployment">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {dep.deployment_models.map((m) => (
              <div key={m} className="rounded-sm border px-2 py-2 flex items-center gap-2" style={{ borderColor: GREEN + "40", background: "rgba(7,27,43,0.55)" }}>
                <Globe2 size={12} style={{ color: GREEN }} />
                <div className="text-[12px] text-[#F6F8FA]">{m}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11.5px] text-[#DCE3E8]">Hosted options: {dep.hosted_options.join(" · ")}</div>
        </Card>
      )}

      {tab === "twin" && twin && (
        <Card title="National Digital Twin Expansion™" accent={BLUE} testId="xvi-twin">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {twin.domains.map((d) => (
              <div key={d.key} className="rounded-sm border px-2 py-1.5" style={{ borderColor: BLUE + "40", background: "rgba(7,27,43,0.55)" }}>
                <div className="text-[12px] font-semibold text-[#F6F8FA]">{d.label}</div>
                <div className="text-[10.5px] text-[#B3C2CC]">Layers {d.layers_available} · Coverage {d.coverage_pct}%</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "compliance" && comp && (
        <Card title="International Compliance Centre™" accent={GREEN} testId="xvi-compliance">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {comp.frameworks.map((f) => (
              <div key={f.key} className="rounded-sm border p-2" style={{ borderColor: GREEN + "35", background: "rgba(7,27,43,0.55)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">{f.label}</div>
                  <Chip color={GREEN}>{f.adherence_pct}%</Chip>
                </div>
                <div className="text-[10.5px] text-[#B3C2CC]">Obligations {f.obligations} · Reports {f.reports_generated}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "resource" && ri && (
        <Card title={`National Resource Intelligence™ · ${ri.total_open} open advisory findings`} accent={AMBER} testId="xvi-resource">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ri.findings.map((f) => (
              <div key={f.id} className="rounded-sm border p-2" style={{ borderColor: AMBER + "35", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">{f.category}</div>
                  <Chip color={f.severity === "high" ? RED : f.severity === "medium" ? AMBER : GREEN} filled>{f.severity}</Chip>
                </div>
                <div className="text-[10.5px] text-[#B3C2CC]">Owner: {f.owner} · Instances: {f.instances}</div>
                <div className="text-[10.5px] text-[#7C93A5] italic mt-1">{f.recommendation}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "studio" && (
        <>
          <Card title="Executive Demonstration Studio™" accent={PURPLE} testId="xvi-studio">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {studio.map((p) => (
                <button key={p.key} onClick={() => openStudio(p.key)}
                        className="rounded-sm border px-2 py-2 text-left"
                        style={{ borderColor: PURPLE + "40", background: (selectedStudio?.key === p.key) ? PURPLE + "20" : "rgba(7,27,43,0.55)" }}
                        data-testid={`studio-${p.key}`}>
                  <div className="text-[12.5px] font-semibold text-[#F6F8FA]">{p.title}</div>
                  <div className="text-[10.5px] text-[#B3C2CC]">{p.density} density · {p.terminology}</div>
                </button>
              ))}
            </div>
          </Card>
          {selectedStudio && (
            <Card title={`Studio profile · ${selectedStudio.title}`} accent={PURPLE}>
              <div className="text-[12px] text-[#DCE3E8] mb-2">Navigation: {selectedStudio.adjusted_navigation.join(" · ")}</div>
              <div className="text-[12px] text-[#DCE3E8] mb-2">Security level: <span className="font-mono text-[#F6F8FA]">{selectedStudio.security_level}</span></div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {selectedStudio.sample_kpis.map((k) => (
                  <div key={k.label} className="rounded-sm border px-2 py-1" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                    <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{k.label}</div>
                    <div className="text-[13px] font-bold text-[#F6F8FA]">{k.value}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {tab === "knowledge" && kcats && (
        <Card title="Government Knowledge Repository™" accent={GOLD} testId="xvi-knowledge">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {kcats.categories.map((c) => (
              <div key={c.key} className="rounded-sm border px-2 py-2" style={{ borderColor: GOLD + "40", background: "rgba(7,27,43,0.55)" }}>
                <div className="text-[12.5px] font-semibold text-[#F6F8FA]">{c.label}</div>
                <div className="text-[13px] font-bold" style={{ color: GOLD }}>{c.documents.toLocaleString()}</div>
                <div className="text-[10px] text-[#7C93A5]">documents</div>
              </div>
            ))}
          </div>
          {kcats.ai_answering && <div className="mt-2 text-[11px] text-[#DCE3E8]"><Sparkles size={11} className="inline mr-1" style={{ color: PURPLE }} />AI-powered question answering enabled across the repository.</div>}
        </Card>
      )}

      {tab === "observatory" && obs && (
        <Card title="National Performance Observatory™" accent={GREEN} testId="xvi-observatory">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {obs.panels.map((p) => (
              <div key={p.key} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "40", background: "rgba(7,27,43,0.55)" }} data-testid={`obs-${p.key}`}>
                <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{p.label}</div>
                <div className="text-[14px] font-bold text-[#F6F8FA]" style={{ color: GREEN }}>
                  {p.unit === "USD" ? `$${Number(p.value).toLocaleString()}` : Number(p.value).toLocaleString()}
                </div>
                <div className="text-[10px] text-[#7C93A5]">{p.unit}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "commercial" && comm && (
        <Card title="Global Commercial Positioning™" accent={GOLD} testId="xvi-commercial">
          <div className="text-[13px] text-[#DCE3E8] mb-3">{comm.positioning}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
            {comm.audiences.map((a) => (
              <div key={a} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GOLD + "40", background: "rgba(7,27,43,0.55)" }}>
                <div className="text-[12px] text-[#F6F8FA]">{a}</div>
              </div>
            ))}
          </div>
          <div className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#7C93A5] mb-1">Deployment models</div>
          <div className="flex flex-wrap gap-1">
            {comm.deployment_models.map((m) => <Chip key={m} color={PURPLE}>{m}</Chip>)}
          </div>
        </Card>
      )}

      {tab === "marketplace" && market && (
        <Card title="Federation Marketplace™" accent={PURPLE} testId="xvi-marketplace">
          <div className="text-[11.5px] text-[#DCE3E8] mb-2">Automatic sovereign platform onboarding · federation ready</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {market.marketplace.map((p) => (
              <div key={p.key} className="rounded-sm border px-2 py-2" style={{ borderColor: PURPLE + "40", background: "rgba(7,27,43,0.55)" }} data-testid={`market-${p.key}`}>
                <div className="text-[12.5px] font-semibold text-[#F6F8FA]">{p.label}</div>
                <div className="text-[10.5px] text-[#B3C2CC]">{p.domain}</div>
                <Chip color={AMBER}>federation-ready</Chip>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "acceptance" && ack && (
        <Card title="Executive Acceptance Report — Phase XVI" accent={GREEN} testId="xvi-acceptance">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              ["Zero regression",         ack.zero_regression],
              ["Phases operational",      ack.phases_operational?.join(", ")],
              ["APIs healthy",            String(ack.apis_healthy)],
              ["Dashboards functional",   String(ack.dashboards_functional)],
              ["QR verification",         ack.qr_verification],
              ["Digital signatures",      ack.digital_signatures],
              ["AI briefing",             ack.ai_briefing],
              ["Benchmarking",            ack.benchmarking],
              ["Public verification",     ack.public_verification],
              ["Water360 federation",     ack.water360_federation],
              ["Executive presentation",  ack.executive_presentation_mode],
              ["Federation architecture", ack.federation_architecture],
              ["Commercial deployment",   ack.commercial_deployment],
            ].map(([k, v]) => (
              <div key={k} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{k}</div>
                <div className="text-[11.5px] text-[#F6F8FA]">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10.5px] text-[#7C93A5] font-mono break-all">SHA-256: {ack.digital_signature_sha256}</div>
        </Card>
      )}

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1">
        Cadastre360™ · Phase XVI · Sovereign Trust & International Readiness · zero regression
      </div>
    </div>
  );
};

export default PhaseXVICentre;
